'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import Link from '../components/SafeLink';

interface Job {
  id: string;
  year: number;
  dept: string;
  type: string;
  status: string;
  title: string;
  location: string;
  image: string;
  desc: string;
  tags: string[];
  reqs: string[];
  resp: string[];
  perks: string[];
}

const JOBS: Job[] = [
  {
    id: 'j1', year: 2025, dept: 'Рекрутинг', type: 'офис', status: 'Открыта',
    title: 'Recruitment Manager', location: 'Грозный, офис',
    image: 'https://avatars.mds.yandex.net/i?id=7e090b88f88cb0a49ef6d3e542ef2d70_l-4801254-images-thumbs&n=13',
    desc: 'Управлять командой RPO-рекрутеров, отвечать за качество и скорость найма.',
    tags: ['RPO', 'team-lead', 'on-site'],
    reqs: ['Опыт управления командой от 2 лет', 'Опыт в mass/RPO', 'Знание BI (PowerBI, Tableau)'],
    resp: ['Ведение команды', 'Контроль KPI', 'Коммуникация с клиентами'],
    perks: ['ДМС со стоматологией', 'Гибкий график', 'Обучение/сертификации']
  },
  {
    id: 'j2', year: 2025, dept: 'Продажи', type: 'офис', status: 'Закрыта',
    title: 'Менеджер по продажам RPO', location: 'Гудермес, офис',
    image: 'https://ucc-msk.ru/data/prich-pok-firm2.png',
    desc: 'Продавать аутсорсинг-сервисы, развивать клиентскую базу в сегменте масс-маркет.',
    tags: ['B2B', 'массовый подбор'],
    reqs: ['Опыт продаж B2B-услуг от 3 лет', 'Знание цикла сделки', 'Умение работать с CRM'],
    resp: ['Лидогенерация', 'Проведение встреч', 'Выполнение плана'],
    perks: ['Фикс + %', 'Оплата связи', 'Корпоративный ноутбук']
  },
  {
    id: 'j3', year: 2025, dept: 'Разработка', type: 'удаленно', status: 'Открыта',
    title: 'Frontend Developer', location: 'Удаленно',
    image: 'https://avatars.mds.yandex.net/i?id=72d230cb7824afea297a682be52f2a77_l-4903222-images-thumbs&ref=rim&n=13&w=1500&h=929',
    desc: 'Разработка пользовательских интерфейсов для платформы INTERIM.',
    tags: ['React', 'Next.js', 'JS'],
    reqs: ['Опыт React/Next.js от 2 лет', 'Верстка Tailwind CSS'],
    resp: ['Разработка новых фич', 'Code review', 'Взаимодействие с бэкендом'],
    perks: ['Полностью удалённая работа', 'Офис по желанию', 'Корпоративные мероприятия']
  },
  {
    id: 'j4', year: 2025, dept: 'IT/DevOps', type: 'офис', status: 'Открыта',
    title: 'DevOps Engineer', location: 'Грозный, офис',
    image: 'https://avatars.dzeninfra.ru/get-zen_doc/4963481/pub_6423e69de1fa7f286487492b_6423e925b2be13638d06396a/scale_1200',
    desc: 'Поддержка и развитие IT-инфраструктуры.',
    tags: ['AWS', 'Kubernetes', 'CI/CD'],
    reqs: ['Опыт работы с облаками', 'Знание Docker', 'CI/CD'],
    resp: ['Поддержка серверов', 'Развертывание ПО', 'Автоматизация'],
    perks: ['Гибкий график', 'Доступ к платным курсам', 'Оплата co-working']
  },
  {
    id: 'j5', year: 2025, dept: 'Рекрутинг', type: 'удаленно', status: 'Закрыта',
    title: 'Sourcer', location: 'Удаленно',
    image: 'https://avatars.mds.yandex.net/i?id=ea813b73eccf0f04d65c19cf6f798ea5_l-4885183-images-thumbs&n=13',
    desc: 'Поиск и привлечение кандидатов для IT-вакансий.',
    tags: ['IT-подбор', 'удалёнка'],
    reqs: ['Опыт в IT-рекрутинге от 1 года', 'Знание boolean search'],
    resp: ['Поиск кандидатов', 'Коммуникация', 'Ведение базы'],
    perks: ['% от закрытия', 'Обучение сорсингу']
  },
  {
    id: 'j6', year: 2025, dept: 'HR/Рекрутинг', type: 'офис', status: 'Открыта',
    title: 'HR-generalist', location: 'Грозный, офис',
    image: 'https://img.kapital.kz/Fjn5B8hGtx8/bG9jYWw6Ly8vZC85L2UvYS9jL2ZiMjRiYWNmNjc5N2UxYmE4MDE1OTVkZDg1ZC5qcGc',
    desc: 'Ведение кадрового делопроизводства, HR-процессов.',
    tags: ['Кадры', 'КДП', 'HR-процессы'],
    reqs: ['Опыт работы от 2 лет', 'Знание ТК РФ', 'Ведение КДП'],
    resp: ['Приём/увольнение', 'HR-отчетность', 'Организация корпоративов'],
    perks: ['Гибкий график', 'ДМС']
  },
];

function badgeClass(status: string): string {
  if (status === 'Открыта') return 'badge ok';
  if (status === 'На паузе') return 'badge warn';
  return 'badge err';
}

const Kpis = () => {
  const kpiData = [
    { label: 'открытых вакансий', value: JOBS.filter(j => j.status === 'Открыта').length },
    { label: 'закрытий в месяц', value: 15 },
    { label: 'средний срок найма, дн.', value: 24 },
    { label: 'резюме/вакансию', value: 4.2 }
  ];
  const counterRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLDivElement;
            const to = Number(el.dataset.to);
            const duration = 900;
            const start = performance.now();
            const step = (now: number) => {
              const progress = Math.min(1, (now - start) / duration);
              const textValue = (to === 4.2) ? (to * progress).toFixed(1) : Math.floor(to * progress);
              el.textContent = `${textValue}`;
              if (progress < 1) {
                requestAnimationFrame(step);
              }
            };
            requestAnimationFrame(step);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.5 }
    );
    counterRefs.current.forEach(ref => {
      if (ref) {
        observer.observe(ref);
      }
    });
    return () => {
      counterRefs.current.forEach(ref => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, []);

  return (
    <section id="kpi" className="grid gap-6 md:grid-cols-4">
      {kpiData.map((kpi, index) => (
        <article key={kpi.label} className="card rounded-2xl p-6 text-center">
          <div
            ref={el => { counterRefs.current[index] = el; }}
            className="text-5xl font-extrabold counter"
            data-to={kpi.value}
          >
            0
          </div>
          <div className="text-xs text-gray-300 mt-1">{kpi.label}</div>
        </article>
      ))}
    </section>
  );
};

interface JobCardProps {
  job: Job;
  onOpenModal: (job: Job) => void;
}
const JobCard = ({ job, onOpenModal }: JobCardProps) => {
  const { title, year, dept, type, status, location, image, desc, tags } = job;
  return (
    <article className="card rounded-2xl overflow-hidden flex flex-col">
      <img
        src={image}
        alt=""
        style={{ width: '100%', height: '11rem', objectFit: 'cover' }}
        className="w-full h-44 object-cover"
      />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xl font-semibold">{title}</h3>
          <span className="tag">{year}</span>
        </div>
        <div className="mt-2 text-xs flex flex-wrap gap-2">
          <span className="tag">{dept}</span>
          <span className="tag">{type}</span>
          <span className={`${badgeClass(status)}`}>{status}</span>
        </div>
        <p className="text-sm text-gray-200 mt-3 line-clamp-3">{desc}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {tags.map(t => <span key={t} className="tag">{t}</span>)}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="text-sm text-gray-300">{location}</div>
          <button className="btn btn-accent text-sm" onClick={() => onOpenModal(job)}>Подробнее</button>
        </div>
      </div>
    </article>
  );
};

interface JobModalProps {
  job: Job;
  onClose: () => void;
}
const JobModal = ({ job, onClose }: JobModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage('');

    const form = e.target as HTMLFormElement;
    const formData = {
      name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
      email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
      phone: (form.elements.namedItem('phone') as HTMLInputElement)?.value,
      link: (form.elements.namedItem('link') as HTMLInputElement)?.value,
      experience: (form.elements.namedItem('experience') as HTMLTextAreaElement)?.value,
      vacancyTitle: job.title,
      vacancyLocation: job.location,
    };

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: formData }),
      });

      if (response.ok) {
        setStatusMessage('✅ Спасибо! Отклик успешно отправлен.');
        form.reset();
      } else {
        const errorData = await response.json();
        setStatusMessage(`❌ Ошибка: ${errorData.error?.message || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      setStatusMessage('❌ Произошла ошибка сети. Попробуйте ещё раз.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  }; // 

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" onClick={onClose}>
      <div className="rounded-2xl p-6 max-w-3xl w-[92%] relative max-h-[90vh] overflow-y-auto bg-blue-900" onClick={e => e.stopPropagation()}>
        <button className="absolute right-3 top-3 px-2 py-1 rounded-lg btn-ghost" onClick={onClose}>✕</button>
        <div className="grid gap-4 md:grid-cols-2">
          <img
            src={job.image}
            alt=""
            style={{ width: '100%', height: '14rem', objectFit: 'cover' }}
            className="rounded-xl w-full h-56 object-cover"
          />
          <div>
            <h3 className="text-2xl font-bold">{job.title}</h3>
            <div className="mt-2 text-xs flex flex-wrap gap-2">
              <span className="tag">{job.dept}</span>
              <span className="tag">{job.type}</span>
              <span className={`${badgeClass(job.status)}`}>{job.status}</span>
              <span className="tag">{job.year}</span>
            </div>
            <p className="text-sm text-gray-200 mt-3">{job.desc}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {job.tags.map(t => <span key={t} className="tag">{t}</span>)}
            </div>
            <div className="mt-4 flex gap-2">
              <a href="#" className="btn btn-ghost">Скачать описание (PDF)</a>
              <a href="#apply" className="btn btn-accent">Отправить отклик</a>
            </div>
          </div>
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div>
            <h4 className="font-semibold mb-2">Требования</h4>
            <ul className="text-sm text-gray-200 space-y-2">
              {job.reqs.map(r => <li key={r}>• {r}</li>)}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Обязанности</h4>
            <ul className="text-sm text-gray-200 space-y-2">
              {job.resp.map(r => <li key={r}>• {r}</li>)}
            </ul>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-semibold mb-2">Что мы предлагаем</h4>
            <ul className="text-sm text-gray-200 space-y-2">
              {job.perks.map(p => <li key={p}>• {p}</li>)}
            </ul>
          </div>
        </div>
        <form
          id="apply-form"
          className="mt-6 simple-card grid gap-4 md:grid-cols-2"
          onSubmit={handleSubmit}
        >
          <input name="name" className="rounded-xl px-4 py-3 text-gray-900" placeholder="Имя и фамилия*" required />
          <input name="email" className="rounded-xl px-4 py-3 text-gray-900" placeholder="E-mail*" type="email" required />
          <input name="phone" className="rounded-xl px-4 py-3 text-gray-900" placeholder="Телефон*" pattern="^[0-9+()\-\s]{6,}$" required />
          <input name="link" className="rounded-xl px-4 py-3 text-gray-900" placeholder="Ссылка на профиль (hh/LinkedIn/GitHub)" />
          <textarea name="experience" className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2" rows={4} placeholder="Коротко о релевантном опыте" />
          <div className="text-xs text-gray-300 md:col-span-2">Отклик на вакансию: <b>{job.title}</b> ({job.location})</div>
          <button type="submit" className="btn btn-accent md:col-span-2" disabled={isSubmitting}>
            {isSubmitting ? 'Отправка...' : 'Отправить отклик'}
          </button>
          {statusMessage && <div className="md:col-span-2 text-center text-sm">{statusMessage}</div>}
        </form>
      </div>
    </div>
  );
};

const departments = [...new Set(JOBS.map(j => j.dept))];
const types = [...new Set(JOBS.map(j => j.type))];
const statuses = [...new Set(JOBS.map(j => j.status))];

// --- ИЗМЕНЕННЫЙ БЛОК: Задан полный список локаций вручную ---
const ALL_LOCATIONS = [
  'Грозный, офис',
  'Гудермес, офис',
  'Удаленно',
  'Шали, офис', // Добавлено
  'Ачхой-Мартан, офис', // Добавлено
  'Серноводск, офис', // Добавлено
  'Аргун, офис', // Добавлено
];
const locations = ALL_LOCATIONS; // Используем полный список
// -----------------------------------------------------------

export default function JobsPage() {
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [location, setLocation] = useState('');
  const [sort, setSort] = useState('year');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  const filteredJobs = useMemo(() => {
    let items = JOBS.filter(j => {
      const q = query.toLowerCase();
      const matchesQuery = !q || (j.title + ' ' + j.desc + ' ' + j.tags.join(' ') + ' ' + j.location).toLowerCase().includes(q);
      const matchesDept = !department || j.dept === department;
      const matchesType = !type || j.type === type;
      const matchesStatus = !status || j.status === status;
      const matchesLocation = !location || j.location === location;
      return matchesQuery && matchesDept && matchesType && matchesStatus && matchesLocation;
    });

    switch (sort) {
      case 'year':
        items.sort((a, b) => b.year - a.year);
        break;
      case 'status':

        items.sort((a, b) => a.status.localeCompare(b.status));
        break;
      case 'name':
        items.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break;
    }
    return items;
  }, [query, department, type, status, location, sort]);

  return (
    <>
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
        <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <Link href="#jobs" className="chip px-3 py-1 rounded-full hover:bg-white/25">Все вакансии</Link>
              <Link href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">FAQ</Link>
              <Link href="#cta" className="chip px-3 py-1 rounded-full hover:bg-white/25">Не нашли подходящую?</Link>
            </nav>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Вакансии INTERIM</h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Присоединяйтесь к команде, которая меняет подход к рекрутингу и HR-сервисам.
              Мы сотрудничаем с лидерами в электронной коммерции, ритейле и IT.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link href="#jobs" className="btn btn-ghost">Смотреть вакансии</Link>
              <Link href="#cta" className="btn btn-accent">Отправить резюме</Link>
            </div>
          </div>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <Kpis />
          <section id="jobs" className="scroll-mt-32">
            <div className="mt-4 grid gap-3 md:grid-cols-6">
              <input
                id="q"
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                placeholder="Поиск по названию/локации/тегам…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <select
                id="dept"
                className="rounded-xl px-4 py-3 text-gray-900 min-w-[200px] whitespace-nowrap"
                value={department}
                onChange={e => setDepartment(e.target.value)}
              >
                <option value="">Отдел: все</option>
                {departments.map(dept => <option key={dept}>{dept}</option>)}
              </select>

              <select
                id="type"
                className="rounded-xl px-4 py-3 text-gray-900 min-w-[200px] whitespace-nowrap"
                value={type}
                onChange={e => setType(e.target.value)}
              >
                <option value="">Тип: все</option>
                {types.map(t => <option key={t}>{t}</option>)}
              </select>

              <select
                id="status"
                className="rounded-xl px-4 py-3 text-gray-900 min-w-[200px] whitespace-nowrap"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="">Статус: все</option>
                {statuses.map(st => <option key={st}>{st}</option>)}
              </select>

              <select
                id="loc"
                className="rounded-xl px-4 py-3 text-gray-900 min-w-[200px] whitespace-nowrap"
                value={location}
                onChange={e => setLocation(e.target.value)}
              >
                <option value="">Локация: все</option>
                {locations.map(loc => <option key={loc}>{loc}</option>)}
              </select>

              <select
                id="sort"
                className="rounded-xl px-4 py-3 text-gray-900 min-w-[220px] whitespace-nowrap"
                value={sort}
                onChange={e => setSort(e.target.value)}
              >
                <option value="year">Сортировать: по году ↓</option>
                <option value="status">по статусу</option>
                <option value="name">по названию</option>
              </select>
            </div>


            <div id="grid" className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredJobs.length > 0 ? (
                filteredJobs.map(job => (
                  <JobCard key={job.id} job={job} onOpenModal={setSelectedJob} />
                ))
              ) : (
                <p id="empty" className="text-gray-300 md:col-span-3">Вакансии не найдены — измените фильтры.</p>
              )}
            </div>
          </section>

          <section id="faq" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ для кандидатов</h2>
            <div className="space-y-3">
              <details className="simple-card">
                <summary className="cursor-pointer font-semibold">Как проходит процесс найма?</summary>
                <p className="mt-2 text-sm text-gray-200">Скрининг резюме &gt; телефонное интервью с HR &gt; собеседование с руководителем &gt; оффер.</p>
              </details>
              <details className="simple-card">
                <summary className="cursor-pointer font-semibold">Какие есть льготы?</summary>
                <p className="mt-2 text-sm text-gray-200">ДМС, оплата мобильной связи, гибкий график, оплата обучения и сертификаций, коворкинг.</p>
              </details>
              <details className="simple-card">
                <summary className="cursor-pointer font-semibold">Вы работаете удалённо?</summary>
                <p className="mt-2 text-sm text-gray-200">Для большинства позиций да. Мы работаем в гибридном формате — офис в Грозном.</p>
              </details>
            </div>
          </section>

          <section
            id="cta"
            className="simple-card flex flex-col md:flex-row md:items-center md:justify-between scroll-mt-32"
          >
            <div>
              <h3 className="text-2xl font-bold">Не нашли подходящую?</h3>
              <p className="text-sm text-gray-200 mt-1">Отправьте нам резюме, и мы вернёмся с предложением, если появится подходящая позиция.</p>
            </div>
            <Link href="/contacts" className="btn btn-accent mt-4 md:mt-0">Отправить резюме</Link>
          </section>
        </main>
        {selectedJob && <JobModal job={selectedJob} onClose={() => setSelectedJob(null)} />}
      </div>
    </>
  );
}